// ===================================================================
// MarketForge — Strike Decision Card (extracted Iter118).
//
// Renders the legacy detailed Strike Decision breakdown — gates, WES
// alignment, GEX alignment, DO/DON'T, plain English, and the "Save
// Decision" journal action. Surfaced behind the Trade Verdict Card's
// "Show detailed breakdown" toggle.
//
// Anti-flicker: dataSymbolRef only clears state on a true symbol
// change. Strike / engine / freshness updates keep the prior frame
// painted while the new payload arrives.
// ===================================================================

import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Activity, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Iter38 — Save Decision action. Self-contained so the parent card stays
// declarative. POSTs to /forge/journal/decisions; shows status pill on
// success / dedup / error.
function StrikeDecisionSaveAction({ data, disabled }) {
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);   // {kind:"saved"|"deduped"|"error", id?, msg?}
  const [notes, setNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);

  const handleSave = async () => {
    if (disabled || saving) return;
    setSaving(true);
    setResult(null);
    try {
      const body = { decision: data };
      if (notes.trim()) body.notes = notes.trim();
      const r = await axios.post(`${API}/forge/journal/decisions`, body);
      if (r.data?.deduped) {
        setResult({ kind: "deduped", id: r.data.row?.id,
                     msg: "Already saved within the last minute." });
      } else {
        setResult({ kind: "saved", id: r.data.row?.id,
                     msg: "Decision saved to journal." });
        setNotes("");
        setShowNotes(false);
      }
    } catch (e) {
      setResult({ kind: "error",
                   msg: e?.response?.data?.detail || e.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  // Auto-clear status after 6 seconds so the rail stays clean.
  useEffect(() => {
    if (!result) return;
    const id = setTimeout(() => setResult(null), 6000);
    return () => clearTimeout(id);
  }, [result]);

  return (
    <div className="mt-3 space-y-1.5">
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          data-testid="forge-strike-decision-save"
          onClick={handleSave}
          disabled={disabled || saving}
          className={`flex-1 text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1.5 rounded-sm border transition-colors ${
            disabled
              ? "bg-[#08080A] text-[#5F5F66] border-[#26262B] cursor-not-allowed"
              : saving
                ? "bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/30"
                : "bg-[#00FF88]/15 text-[#00FF88] border-[#00FF88]/40 hover:bg-[#00FF88]/25"}`}
        >
          {saving ? "Saving…" : "Save Decision"}
        </button>
        <button
          type="button"
          data-testid="forge-strike-decision-toggle-notes"
          onClick={() => setShowNotes(v => !v)}
          disabled={disabled}
          className={`text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1.5 rounded-sm border ${
            disabled
              ? "bg-[#08080A] text-[#5F5F66] border-[#26262B] cursor-not-allowed"
              : "bg-[#0A0A0E] text-[#A0A0A8] border-[#26262B] hover:border-[#FFB800]/40 hover:text-[#FFB800]"}`}
          title="Add a note before saving"
        >+ Note</button>
      </div>
      {showNotes && !disabled && (
        <textarea
          data-testid="forge-strike-decision-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value.slice(0, 1000))}
          placeholder="Optional note (max 1000 chars)…"
          className="w-full text-[10px] font-mono bg-[#0A0A0E] border border-[#26262B] rounded-sm px-2 py-1.5 text-white placeholder-[#5F5F66] focus:outline-none focus:border-[#FFB800]/40"
          rows={2}
        />
      )}
      {result && (
        <div data-testid="forge-strike-decision-save-result"
             className={`text-[9px] font-mono px-2 py-1 rounded-sm border ${
               result.kind === "saved"  ? "bg-[#00FF88]/10 text-[#00FF88] border-[#00FF88]/30" :
               result.kind === "deduped" ? "bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/30" :
                                            "bg-[#FF3366]/10 text-[#FF3366] border-[#FF3366]/30"}`}>
          {result.msg}
        </div>
      )}
      {disabled && (
        <div className="text-[8px] font-mono text-[#5F5F66] italic">
          Pick a candidate strike to enable journal save.
        </div>
      )}
    </div>
  );
}

export function StrikeDecisionCard({ symbol, wesEngine, candidateStrike, strategy,
                                     freshnessState, onEvaluate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  // Iter117.8 — Anti-flicker: only wipe stale state when SYMBOL changes.
  // Strike / engine / freshness changes keep prior data painted.
  const dataSymbolRef = useRef(null);
  useEffect(() => {
    if (!symbol) return;
    let alive = true;
    const symbolChanged = symbol !== dataSymbolRef.current;
    if (symbolChanged) {
      setData(null);
      setErr(null);
    }
    setLoading(true);
    const params = { symbol, strategy,
                      engine: wesEngine === "legacy" ? "legacy" : "parity",
                      freshness_state: freshnessState || "FRESH" };
    if (candidateStrike) params.candidate_strike = candidateStrike;
    axios.get(`${API}/forge/strike-decision`, { params })
      .then(r => {
        if (!alive) return;
        if (r.data?.symbol && r.data.symbol.toUpperCase() !== symbol.toUpperCase()) {
          setErr(`STRIKE DECISION DATA MISMATCH — ${r.data.symbol} returned for ${symbol}`);
          return;
        }
        setData(r.data);
        setErr(null);
        dataSymbolRef.current = symbol;
      })
      .catch(e => { if (alive) setErr(e?.response?.data?.detail || e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [symbol, wesEngine, candidateStrike, strategy, freshnessState]);

  const STATUS_STYLES = {
    ACCEPT:             { bg: "bg-[#00FF88]/15", border: "border-[#00FF88]", text: "text-[#00FF88]" },
    CAUTION:            { bg: "bg-[#FFB800]/15", border: "border-[#FFB800]", text: "text-[#FFB800]" },
    CAUTION_DATA_STALE: { bg: "bg-[#FFB800]/15", border: "border-[#FFB800]", text: "text-[#FFB800]" },
    REJECT:             { bg: "bg-[#FF3366]/15", border: "border-[#FF3366]", text: "text-[#FF3366]" },
    BLOCKED:            { bg: "bg-[#FF3366]/15", border: "border-[#FF3366]", text: "text-[#FF3366]" },
    NO_TRADE_ROI_FAIL:  { bg: "bg-[#FF3366]/15", border: "border-[#FF3366]", text: "text-[#FF3366]" },
  };
  const styles = data ? (STATUS_STYLES[data.status] || STATUS_STYLES.CAUTION) : null;
  const gateBadge = (label, passing, suffix = "") => (
    <span className={`text-[8px] font-bold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-sm border ${
      passing === true  ? "bg-[#00FF88]/15 text-[#00FF88] border-[#00FF88]/40" :
      passing === false ? "bg-[#FF3366]/15 text-[#FF3366] border-[#FF3366]/40" :
      "bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/30"}`}>
      {label}{suffix ? `: ${suffix}` : ""}
    </span>
  );

  return (
    <div data-testid="forge-strike-decision-card"
         className="bg-[#121216] border border-[#26262B] rounded-sm p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Activity size={14} className="text-[#FFB800]" />
          <h3 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8A8A93]">
            Strike Decision
          </h3>
          {loading && <Loader2 size={12} className="animate-spin text-[#8A8A93]" />}
        </div>
        <span className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#5F5F66]">
          PARITY v1 · GEX PATH · ROI
        </span>
      </div>

      {err && (
        <div data-testid="forge-strike-decision-error"
             className="text-[10px] font-mono text-[#FF3366] bg-[#FF3366]/10 border border-[#FF3366]/30 rounded-sm px-2 py-1.5">
          {err}
        </div>
      )}
      {!err && !data && loading && (
        <div className="text-[10px] font-mono text-[#5F5F66] bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1.5 flex items-center gap-2">
          <Loader2 size={12} className="animate-spin" /> Evaluating strike for {symbol}…
        </div>
      )}
      {!err && !loading && !data && (
        <div className="text-[10px] font-mono text-[#5F5F66] bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1.5">
          Candidate strike not supplied. Select one manually or wait for suggestions.
        </div>
      )}

      {data && styles && (
        <>
          {/* A. Final Status */}
          <div data-testid="forge-strike-decision-status"
               className={`${styles.bg} border ${styles.border} rounded-sm px-3 py-2 mb-3`}>
            <div className={`text-[13px] font-bold uppercase tracking-[0.15em] ${styles.text}`}>
              {data.status}
            </div>
            <div className={`text-[10px] font-mono ${styles.text} opacity-80`}>
              {data.action_label}
            </div>
            <div className="mt-1 flex items-center gap-2 text-[8px] font-mono text-[#8A8A93]">
              <span>SCORE {data.decision_score}</span>
              <span>·</span>
              <span>CONFIDENCE {data.confidence}</span>
            </div>
          </div>

          {/* B. Candidate Strike */}
          {data.candidate_strike != null && (
            <div className="bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1.5 mb-3">
              <div className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#5F5F66] mb-1">
                Candidate {data.strategy?.replace("WHEEL_ITM_", "")}
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[9px] font-mono">
                <div className="text-[#8A8A93]">Strike</div>
                <div className="text-right text-white">${Number(data.candidate_strike).toFixed(2)}</div>
                {data.expiration && (<><div className="text-[#8A8A93]">Exp</div><div className="text-right text-white">{data.expiration}</div></>)}
                <div className="text-[#8A8A93]">ROI bid</div>
                <div className={`text-right font-bold ${data.roi_gate.passed ? "text-[#00FF88]" : "text-[#FF3366]"}`}>
                  {data.roi_gate.actual_bid != null ? `${(data.roi_gate.actual_bid * 100).toFixed(2)}%` : "—"}
                  {data.roi_gate.actual_bid != null && (data.roi_gate.passed ? " ✓" : " ✕")}
                </div>
              </div>
            </div>
          )}

          {/* Iter37 — Suggested Strikes. Only shown when the user has not
              selected a candidate strike. Click "Evaluate" routes the strike
              back up to MarketForge so WES-GEX + Strike Decision re-run. */}
          {data.candidate_strike == null && (data.suggested_candidates?.length || 0) > 0 && (
            <div data-testid="forge-strike-decision-suggestions"
                 className="bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-2 mb-3">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#FFB800]">
                  Suggested Strikes
                </div>
                <div className="text-[8px] font-mono text-[#5F5F66]">click Evaluate</div>
              </div>
              <div className="text-[9px] font-mono text-[#8A8A93] leading-snug mb-2">
                Candidate strike not supplied. Select one manually or evaluate one of the suggested ROI-passing strikes below.
              </div>
              <div className="space-y-1.5">
                {data.suggested_candidates.map((s, i) => (
                  <div key={`${s.strike}-${i}`}
                       data-testid={`forge-strike-suggestion-${i}`}
                       className={`rounded-sm border px-1.5 py-1.5 text-[9px] font-mono ${
                         s.below_roi_gate
                           ? "bg-[#FFB800]/5 border-[#FFB800]/30"
                           : "bg-[#0A0A0E] border-[#26262B] hover:border-[#00FF88]/40"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[8px] font-bold uppercase tracking-[0.12em] text-[#8A8A93]">
                          {s.strategy?.replace("WHEEL_ITM_", "")}
                        </span>
                        <span className="text-white font-bold">${Number(s.strike).toFixed(2)}</span>
                        {s.expiration && <span className="text-[#5F5F66]">· {s.expiration}</span>}
                      </div>
                      <button
                        type="button"
                        data-testid={`forge-strike-suggestion-evaluate-${i}`}
                        onClick={() => onEvaluate && onEvaluate(Number(s.strike))}
                        className="text-[8px] font-bold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-sm border bg-[#00FF88]/15 text-[#00FF88] border-[#00FF88]/40 hover:bg-[#00FF88]/25"
                      >Evaluate</button>
                    </div>
                    <div className="grid grid-cols-4 gap-x-1.5 gap-y-0.5 mt-1 text-[8px]">
                      <div className="text-[#8A8A93]">ROI</div>
                      <div className={`col-span-3 text-right font-bold ${
                        s.below_roi_gate ? "text-[#FFB800]" : "text-[#00FF88]"}`}>
                        {s.roi_pct?.toFixed(2)}%{s.below_roi_gate ? " · below gate" : " ✓"}
                      </div>
                      <div className="text-[#8A8A93]">WES</div>
                      <div className="col-span-3 text-right text-white">{s.nearest_wes_level}</div>
                      <div className="text-[#8A8A93]">GEX</div>
                      <div className="col-span-3 text-right text-white">{s.gex_verdict || "—"} · {s.gex_dealer_regime?.replace(/_/g, " ")}</div>
                      <div className="text-[#8A8A93]">Pin / Mag</div>
                      <div className="col-span-3 text-right text-white">{s.pin_risk} / {s.assignment_magnet_risk}</div>
                      <div className="text-[#8A8A93]">Path</div>
                      <div className="col-span-3 text-right text-white">{s.path_quality?.replace(/_/g, " ")}</div>
                      {s.open_interest != null && (
                        <><div className="text-[#8A8A93]">OI</div>
                          <div className="col-span-3 text-right text-white">{s.open_interest}</div></>
                      )}
                    </div>
                    {s.below_roi_gate && (
                      <div className="text-[8px] font-mono text-[#FFB800] mt-1">{s.label}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Empty-state copy when no candidates pass ROI */}
          {data.candidate_strike == null && (data.suggested_candidates?.length || 0) === 0 && (
            <div data-testid="forge-strike-decision-no-suggestions"
                 className="text-[9px] font-mono text-[#FFB800] bg-[#FFB800]/5 border border-[#FFB800]/30 rounded-sm px-2 py-1.5 mb-3">
              No ROI-passing candidates found. Do not force a trade.
            </div>
          )}

          {/* C. Gates */}
          <div className="flex flex-wrap gap-1 mb-3">
            {gateBadge("DATA", data.status === "CAUTION_DATA_STALE" ? null : (data.status === "BLOCKED" && (data.risk_flags || []).some(f => f.startsWith("FRESHNESS_")) ? false : true),
                       data.status === "CAUTION_DATA_STALE" ? "STALE" : "FRESH")}
            {gateBadge("WES", !(data.risk_flags || []).includes("WES_NOT_PARITY"), "PARITY")}
            {gateBadge("ROI", data.roi_gate.passed, data.roi_gate.passed ? "PASS" : "FAIL")}
            {gateBadge("GEX",
              data.gex_alignment.status === "SUPPORTIVE" ? true :
              data.gex_alignment.status === "DANGEROUS" || data.gex_alignment.status === "BLOCKED" ? false : null,
              data.gex_alignment.status)}
            {data.strategy?.includes("CSP") && (
              gateBadge("ASSIGNMENT",
                (data.risk_flags || []).includes("ASSIGNMENT_NOT_ACCEPTED_BUT_PIN_HIGH") ? false : true,
                (data.risk_flags || []).includes("ASSIGNMENT_NOT_ACCEPTED_BUT_PIN_HIGH") ? "NOT ACCEPTED" : "OK")
            )}
          </div>

          {/* D. WES Alignment */}
          <div className="bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1.5 mb-2 text-[9px] font-mono">
            <div className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#5F5F66] mb-0.5">WES Alignment</div>
            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
              <div className="text-[#8A8A93]">Status</div>
              <div className="text-right text-white">{data.wes_alignment.status}</div>
              <div className="text-[#8A8A93]">WES-P / WES-C</div>
              <div className="text-right text-white">{data.wes_alignment.wes_p?.toFixed(1)} / {data.wes_alignment.wes_c?.toFixed(1)}</div>
              <div className="text-[#8A8A93]">Nearest level</div>
              <div className="text-right text-white">{data.wes_alignment.nearest_wes_level}</div>
            </div>
            {data.wes_alignment.structure_read && (
              <div className="text-[#8A8A93] mt-0.5">{data.wes_alignment.structure_read}</div>
            )}
          </div>

          {/* E. GEX Alignment */}
          <div className="bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1.5 mb-2 text-[9px] font-mono">
            <div className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#5F5F66] mb-0.5">GEX Alignment</div>
            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
              <div className="text-[#8A8A93]">Regime</div>
              <div className="text-right text-white">{data.gex_alignment.dealer_regime?.replace(/_/g, " ")}</div>
              <div className="text-[#8A8A93]">Wall</div>
              <div className="text-right text-white">${Number(data.gex_alignment.nearest_gex_wall || 0).toFixed(2)}</div>
              <div className="text-[#8A8A93]">Pin / Magnet</div>
              <div className="text-right text-white">{data.gex_alignment.pin_risk} / {data.gex_alignment.assignment_magnet_risk}</div>
              <div className="text-[#8A8A93]">Path</div>
              <div className="text-right text-white">{data.gex_alignment.path_quality}</div>
            </div>
          </div>

          {/* F. DO / DON'T */}
          {(data.do?.length || data.dont?.length) && (
            <div className="grid grid-cols-2 gap-1 mb-2 text-[9px] font-mono">
              <div className="bg-[#08080A] border border-[#00FF88]/20 rounded-sm px-1.5 py-1">
                <div className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#00FF88] mb-0.5">Do</div>
                {(data.do || []).map((line, i) => (
                  <div key={i} className="text-[#A0A0A8]">• {line}</div>
                ))}
              </div>
              <div className="bg-[#08080A] border border-[#FF3366]/20 rounded-sm px-1.5 py-1">
                <div className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#FF3366] mb-0.5">Don&apos;t</div>
                {(data.dont || []).map((line, i) => (
                  <div key={i} className="text-[#A0A0A8]">• {line}</div>
                ))}
              </div>
            </div>
          )}

          {/* G. Plain English */}
          {data.plain_english && (
            <div data-testid="forge-strike-decision-plain"
                 className="text-[10px] font-mono text-[#A0A0A8] leading-relaxed bg-[#0A0A0E] border border-[#26262B] rounded-sm px-2 py-1.5">
              {data.plain_english}
            </div>
          )}

          {/* H. Save Decision — Iter38 Journal v1. Explicit user click only.
              Disabled when there's no candidate strike (BLOCKED-MISSING). */}
          <StrikeDecisionSaveAction
            data={data}
            disabled={data.candidate_strike == null}
          />
        </>
      )}
    </div>
  );
}

export default StrikeDecisionCard;
